import React from "react";
import { motion, useScroll, useTransform } from "framer-motion";

/**
 * Cinematic Clapboard icon — sits inline inside the BlinkShots hero wordmark.
 * The top slate claps open/close as the user scrolls toward the pricing section.
 * Pure SVG, framer-motion driven, premium metallic finish.
 */
export const Clapboard = ({ scrollRef, sizeClass = "h-[0.65em]" }) => {
  const { scrollYProgress } = useScroll({
    target: scrollRef,
    offset: ["start start", "end start"],
  });

  // Slate rotation — 2 cinematic claps during early scroll, then settle
  const rotate = useTransform(
    scrollYProgress,
    [0, 0.06, 0.12, 0.20, 0.28, 1],
    [-6, -42, -4, -42, 0, 0]
  );

  // Soft red glow pulses when the slate snaps closed — subtle, premium, not neon
  const glowOpacity = useTransform(
    scrollYProgress,
    [0, 0.05, 0.07, 0.12, 0.19, 0.21, 0.28, 1],
    [0.08, 0.08, 0.35, 0.15, 0.15, 0.35, 0.08, 0.08]
  );

  return (
    <span
      className={`relative inline-flex items-center align-middle mx-[0.18em] ${sizeClass} aspect-[5/4] select-none`}
      data-testid="hero-clapboard"
    >
      {/* Soft red glow pulse behind clapboard */}
      <motion.span
        aria-hidden
        className="absolute -inset-3 rounded-2xl pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at center, rgba(225,6,0,0.55), transparent 70%)",
          filter: "blur(18px)",
          opacity: glowOpacity,
        }}
      />

      <svg
        viewBox="0 0 120 96"
        preserveAspectRatio="xMidYMid meet"
        className="relative h-full w-full"
        aria-hidden
      >
        <defs>
          <linearGradient id="clap-body" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1a1a1a" />
            <stop offset="55%" stopColor="#0a0a0a" />
            <stop offset="100%" stopColor="#050505" />
          </linearGradient>
          <linearGradient id="clap-slate" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#1f1f1f" />
            <stop offset="100%" stopColor="#080808" />
          </linearGradient>
          <linearGradient id="clap-gloss" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
          </linearGradient>
          <clipPath id="slate-clip">
            <rect x="6" y="10" width="108" height="32" rx="3" />
          </clipPath>
          <clipPath id="body-clip">
            <rect x="6" y="44" width="108" height="46" rx="5" />
          </clipPath>
        </defs>

        {/* === BASE / BODY === */}
        <rect
          x="6"
          y="44"
          width="108"
          height="46"
          rx="5"
          fill="url(#clap-body)"
          stroke="rgba(255,255,255,0.12)"
          strokeWidth="0.8"
        />
        {/* faint info lines inside body */}
        <g clipPath="url(#body-clip)" opacity="0.45">
          <line x1="14" y1="56" x2="80" y2="56" stroke="rgba(255,255,255,0.35)" strokeWidth="0.6" />
          <line x1="14" y1="64" x2="62" y2="64" stroke="rgba(255,255,255,0.22)" strokeWidth="0.6" />
          <line x1="14" y1="72" x2="74" y2="72" stroke="rgba(255,255,255,0.22)" strokeWidth="0.6" />
          <line x1="14" y1="80" x2="48" y2="80" stroke="rgba(255,255,255,0.18)" strokeWidth="0.6" />
        </g>
        {/* small red status dot */}
        <circle cx="100" cy="58" r="2.6" fill="#E10600" />
        <circle cx="100" cy="58" r="4" fill="#E10600" opacity="0.25" />

        {/* === TOP SLATE (clapper) === */}
        <motion.g
          style={{ rotate, originX: "6px", originY: "44px", transformBox: "fill-box" }}
        >
          {/* slate body */}
          <rect
            x="6"
            y="10"
            width="108"
            height="32"
            rx="3"
            fill="url(#clap-slate)"
            stroke="rgba(255,255,255,0.18)"
            strokeWidth="0.8"
          />
          {/* black + white slanted stripes */}
          <g clipPath="url(#slate-clip)">
            <polygon points="10,10 30,10 22,42 4,42" fill="#f5f5f5" />
            <polygon points="50,10 70,10 62,42 44,42" fill="#f5f5f5" />
            <polygon points="90,10 110,10 102,42 84,42" fill="#f5f5f5" />
          </g>
          {/* glossy top highlight */}
          <rect x="7" y="11" width="106" height="6" rx="2" fill="url(#clap-gloss)" />
          {/* hinge accent */}
          <circle cx="8" cy="43" r="2" fill="#E10600" />
        </motion.g>

        {/* hinge bolt on base */}
        <circle cx="8" cy="43" r="1.6" fill="#3a3a3a" stroke="rgba(255,255,255,0.2)" strokeWidth="0.5" />
        <circle cx="112" cy="43" r="1.6" fill="#3a3a3a" stroke="rgba(255,255,255,0.2)" strokeWidth="0.5" />
      </svg>
    </span>
  );
};

export default Clapboard;
